import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import {
  AccountService,
  FormUserCompletionResponse,
  LicenceService,
  LicenceStreamDto,
  LoginResponse,
} from "../../swagger/api.client.generated";
import { PaymentsService } from "../services/payments.service";

@Injectable({ providedIn: "root" })
export class UserService {
  constructor(
    private licenceService: LicenceService,
    private paymentsService: PaymentsService,
    private accountService: AccountService
  ) {}

  formUserCompletion: FormUserCompletionResponse;

  private userLoginSource = new Subject<boolean>();
  private _licence: string;
  private _isLoggedIn: boolean;
  private _prevUsername: string;
  _showScoreCard: boolean = false;
  userLoginStatusChanged$ = this.userLoginSource.asObservable();

  private emailConfirmedSource = new BehaviorSubject<boolean>(true);
  emailConfirmedChanged$ = this.emailConfirmedSource.asObservable();

  // The email-confirmation banner is mounted once in app.component and only reads getUser() at
  // init - without this, it would keep showing whatever address was current at first mount, even
  // after a later email change on Settings.
  private usernameSource = new BehaviorSubject<string>(null);
  usernameChanged$ = this.usernameSource.asObservable();

  firstCallToService: boolean = true;

  /**
   * Refreshes email-confirmation state from the server - the user may confirm
   * via a link opened in a different tab/device than the one showing the banner.
   */
  refreshEmailConfirmationStatus() {
    if (!this.isUserLoggedIn()) {
      return;
    }
    this.accountService.emailConfirmationStatus().subscribe((result) => {
      const confirmed = result.responseData?.confirmed ?? true;
      this.emailConfirmedSource.next(confirmed);
      const user = this.getUser();
      if (user) {
        user.emailConfirmed = confirmed;
        this.saveUserToLocalStorage(user);
      }
    });
  }

  changeUserAuthentication(isLoggedIn: boolean) {
    this.userLoginSource.next(isLoggedIn);
  }

  saveUser(model: LoginResponse): UserInformation {
    const ui = this.parseModel(model);
    this.saveUserToLocalStorage(ui);
    this._isLoggedIn = true;
    this._prevUsername = ui.username;
    this.changeUserAuthentication(true);
    this.postLoginToNativeShell(ui);
    this.emailConfirmedSource.next(ui.emailConfirmed);
    return this.getUser();
  }

  private postLoginToNativeShell(ui: UserInformation) {
    const rnWebView = (window as any).ReactNativeWebView;
    if (rnWebView && typeof rnWebView.postMessage === "function") {
      rnWebView.postMessage(
        JSON.stringify({
          type: "login",
          token: ui.token,
          user: {
            userGuid: ui.userGuid,
            username: ui.username,
            tokenExpirationDate: ui.tokenExpirationDate,
            onboardingComplete: ui.onboardingComplete,
            emailConfirmed: ui.emailConfirmed,
          },
        })
      );
    }
  }

  saveUserToLocalStorage(ui: UserInformation) {
    localStorage.setItem("currentUser", JSON.stringify(ui));
    localStorage.setItem("username", ui.username);
    localStorage.setItem("isFirstLogin", ui.isFirstLogin ? "true" : "false");
  }

  /**
   * Holds the JWT from registration in sessionStorage (survives the full-page
   * redirect to Stripe and back) without treating the user as logged in -
   * isUserLoggedIn()/getUser() only ever read "currentUser" from localStorage.
   * Real login happens in saveUser(), called once payment is confirmed.
   */
  savePendingAuth(model: LoginResponse) {
    const ui = this.parseModel(model);
    sessionStorage.setItem("pendingAuth", JSON.stringify(ui));
  }

  getPendingAuth(): UserInformation {
    const storedData = JSON.parse(sessionStorage.getItem("pendingAuth"));
    if (!storedData) {
      return;
    }
    return this.parseModel(storedData as LoginResponse);
  }

  hasPendingAuth(): boolean {
    return !!this.getPendingAuth();
  }

  clearPendingAuth() {
    sessionStorage.removeItem("pendingAuth");
  }

  /**
   * Token for outgoing API calls: the real session token once logged in,
   * otherwise the pending (pre-login) token so the Stripe checkout/verify
   * calls during registration can still authenticate.
   */
  getAuthToken(): string {
    const user = this.getUser();
    if (user && user.token) {
      return user.token;
    }
    const pending = this.getPendingAuth();
    return pending ? pending.token : null;
  }

  /**
   * Promotes the pending (pre-login) session into a real, logged-in one.
   * Called once payment is confirmed and the user clicks Continue.
   */
  activatePendingAuth(): boolean {
    const pending = this.getPendingAuth();
    if (!pending) {
      return false;
    }
    this.saveUserToLocalStorage(pending);
    this.clearPendingAuth();
    this._isLoggedIn = true;
    this._prevUsername = pending.username;
    this.changeUserAuthentication(true);
    this.postLoginToNativeShell(pending);
    this.emailConfirmedSource.next(pending.emailConfirmed);
    return true;
  }

  setDisplayScoreCard(showScoreCard: boolean) {
    this._showScoreCard = showScoreCard;
  }

  getDisplayScoreCard() {
    return this._showScoreCard;
  }

  setLicence(value: string) {
    this._licence = value;
  }

  getLicence() {
    return this._licence;
  }

  hasLicence() {
    return this._licence != undefined && this._licence.length > 0;
  }

  isFirstLogin() {
    return localStorage.getItem("isFirstLogin") === "true";
  }

  /**
   * Existing sessions predating this flag have no cached value - treat that
   * as complete so the nav isn't hidden retroactively for already-onboarded users.
   */
  isOnboardingComplete(): boolean {
    const user = this.getUser();
    return !user || user.onboardingComplete !== false;
  }

  /**
   * The account's registration timestamp, or null when it isn't known.
   *
   * `registerDate` arrives typed as a Date on LoginResponse but survives a page reload as
   * whatever JSON.parse produced from localStorage - a string. Normalizing here means callers
   * get a real Date on both paths (fresh login and reload) rather than something that happens
   * to answer to Date methods only in one of them.
   */
  getRegisterDate(): Date | null {
    const registerDate = this.getUser()?.registerDate;
    if (!registerDate) {
      return null;
    }

    // A string with no timezone designator is UTC, not local. RegisterDate is `u.InsertDate`
    // from SQL, which serializes as "2026-08-19T00:37:07" with no trailing Z when its Kind is
    // Unspecified - and `new Date()` reads a bare date-time like that as LOCAL. On UTC+10 that
    // moves registration ten hours earlier, which is enough to land it on the previous calendar
    // day and hand a brand-new account its day-2 Insight on day 1.
    const normalized =
      typeof registerDate === "string" && !/(Z|[+-]\d{2}:?\d{2})$/.test(registerDate)
        ? `${registerDate}Z`
        : registerDate;

    const parsed = new Date(normalized);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  /**
   * FLIP-215: whether the current local day is still the day this account was registered.
   *
   * Compared as local calendar days, not as an elapsed-hours difference: "day 2 of the account"
   * means the next day on the user's own calendar, so somebody registering at 11pm becomes
   * eligible an hour later, not 24 hours later. Deliberately compares local date parts rather
   * than UTC ones - on UTC+10 the UTC date rolls over at 10am local, which would end "day 1"
   * in the middle of the user's first morning.
   *
   * Returns false when the registration date is unknown, so an account that predates the field
   * (or a session that lost it) is treated as established rather than being silently gated -
   * the failure mode that matters here is suppressing Insights for existing users forever.
   */
  isRegistrationDay(): boolean {
    const registerDate = this.getRegisterDate();
    if (!registerDate) {
      return false;
    }

    const now = new Date();
    return (
      registerDate.getFullYear() === now.getFullYear() &&
      registerDate.getMonth() === now.getMonth() &&
      registerDate.getDate() === now.getDate()
    );
  }

  setOnboardingComplete(value: boolean) {
    const user = this.getUser();
    if (user) {
      user.onboardingComplete = value;
      this.saveUserToLocalStorage(user);
      // The native app shell (WebView) keeps its own copy of currentUser and re-injects it into
      // localStorage on every relaunch. It only learns onboardingComplete at login (false there),
      // so without re-posting the updated user here it keeps re-injecting false and OnboardingGuard
      // bounces the user back to /onboarding after they've finished it. Re-posting a login message
      // updates native's persisted session so the completed flag survives an app restart. No-op in
      // a plain browser (no window.ReactNativeWebView).
      this.postLoginToNativeShell(user);
    }
  }

  setPreferredName(value: string) {
    const user = this.getUser();
    if (user) {
      user.preferredName = value;
      this.saveUserToLocalStorage(user);
    }
  }

  /**
   * Called after Settings changes the account email - username doubles as the login email
   * throughout the app (see login.component.ts), so this keeps both in sync.
   *
   * Also updates _prevUsername to match: getUser() re-fires userLoginStatusChanged$ whenever
   * the stored username differs from _prevUsername, as a way of detecting an externally-swapped
   * mobile-app account. Leaving _prevUsername stale here would make the very next getUser() call
   * misread this save as an account swap.
   */
  setUsername(value: string) {
    const user = this.getUser();
    if (user) {
      user.username = value;
      this.saveUserToLocalStorage(user);
      this._prevUsername = value;
      this.usernameSource.next(value);
    }
  }

  getUser(): UserInformation {
    const storedData = JSON.parse(localStorage.getItem("currentUser"));
    // console.log("current user from local storage", storedData);

    const loginRespObj = new LoginResponse();

    if (!storedData) {
      return;
    }

    loginRespObj.token = storedData.token;
    loginRespObj.tokenExpirationDate = storedData.tokenExpirationDate;
    loginRespObj.userGuid = storedData.userGuid;
    loginRespObj.username = storedData.username;
    loginRespObj.registerDate = storedData.registerDate;
    loginRespObj.licenceStream = storedData.licenceStream;
    loginRespObj.preferredName = storedData.preferredName;
    loginRespObj.onboardingComplete = storedData.onboardingComplete;
    loginRespObj.emailConfirmed = storedData.emailConfirmed;

    if (!this._isLoggedIn) {
      this._isLoggedIn = true;
      this._prevUsername = loginRespObj.username;
      this.changeUserAuthentication(true);
    }

    // the user was changed but external forces (mobile app) and we should change user
    if (this._isLoggedIn && this._prevUsername !== loginRespObj.username) {
      this._prevUsername = loginRespObj.username;
      this.changeUserAuthentication(true);
    }

    // TODO - this if statement can be removed for next PROD release
    // handle users who are already logged in but have no licence stream
    if (!loginRespObj.licenceStream && this.firstCallToService) {
      this.firstCallToService = false;
      this.licenceService.getUserLicenceStream().subscribe((licenceStream) => {
        loginRespObj.licenceStream = licenceStream;
        this.saveUserToLocalStorage(this.parseModel(loginRespObj));
        return this.parseModel(loginRespObj as LoginResponse);
      });
    }

    // return this.parseModel(storedData as LoginResponse);
    return this.parseModel(loginRespObj as LoginResponse);
  }

  isUserLoggedIn(): boolean {
    return !!this.getUser();
  }

  /**
   * The signed-in account's stable id, for namespacing per-user local drafts
   * (FLIP-309) - `userGuid` rather than `username`, since a user can change their
   * username/email in Settings without that being a different account.
   */
  getUserId(): string | null {
    return this.getUser()?.userGuid ?? null;
  }

  /**
   * FLIP-309: signing out must make drafts (e.g. ScoreLocalService's namespaced
   * halos/zombies keys) inaccessible, not delete them - so this removes only the
   * auth/session keys it owns rather than blanket-clearing localStorage, which used
   * to destroy every user's drafts on this device on an ordinary sign-out.
   */
  logoutUser() {
    localStorage.removeItem("currentUser");
    localStorage.removeItem("username");
    localStorage.removeItem("isFirstLogin");
    this.clearPendingAuth();
    this.paymentsService.clearCache();
    this.changeUserAuthentication(false);
  }

  logoutOnTokenExp() {
    localStorage.removeItem("currentUser");
    this.paymentsService.clearCache();
  }

  getUserName(): string {
    const user = this.getUser();
    if (user) {
      return user.username;
    }
    return null;
  }

  getPreferredName(): string {
    const user = this.getUser();
    if (user) {
      return user.preferredName;
    }
    return null;
  }  

  getLicenceStream(): LicenceStreamDto {
    const user = this.getUser();
    if (user && user.licenceStream) {
      return user.licenceStream;
    }
    // TODO - this else statement can be removed for next PROD release
    else {
      this.firstCallToService = false;
      this.licenceService.getUserLicenceStream().subscribe((licenceStream) => {
        user.licenceStream = licenceStream;
        this.saveUserToLocalStorage(this.parseModel(user as LoginResponse));
        return user.licenceStream;
      });
    }
    return null;
  }

  isTokenExpired() {
    const ui = this.getUser();
    // console.log("from getuser", ui);
    // console.log("tokenExpiryDate", ui.tokenExpiryDate);
    if (!ui) {
      return;
    }

    var expiryDateLocal = new Date(ui.tokenExpirationDate);

    return new Date() > expiryDateLocal;
    // return (new Date() > ui.tokenExpiryDate);
  }
  private parseModel(model: LoginResponse): UserInformation {
    // console.log("user as model (LoginResponse)", model);
    // console.log("model date", model.tokenExpirationDate);
    if (!model) {
      return;
    }

    const ui = new UserInformation();
    ui.token = model.token;
    // Server-side data (and older accounts created before input was normalized) can still be
    // uppercase/mixed-case - normalize here so it always displays as entered/sentence case.
    ui.username = model.username?.toLowerCase();
    ui.tokenExpirationDate = model.tokenExpirationDate;
    ui.userGuid = model.userGuid;
    ui.registerDate = model.registerDate;
    ui.licenceStream = model.licenceStream;
    ui.isFirstLogin = model.isFirstLogin;
    ui.preferredName = model.preferredName;
    ui.onboardingComplete = model.onboardingComplete;
    ui.emailConfirmed = model.emailConfirmed;
    return ui;
  }
}

export class UserInformation {
  username: string;
  tokenExpirationDate: Date;
  // deprecated
  userGuid: string;
  isFirstLogin: boolean;
  token: string;
  registerDate: Date;
  licenceStream: LicenceStreamDto;
  preferredName: string;
  onboardingComplete: boolean;
  emailConfirmed: boolean;
}
